import React from 'react';

import SearchableTable from '../../global/Search/SearchableTable';
import EmployeeWizard from './EmployeeWizard';

// styling
import './ManageUsersTable.css';
import './EmployeeWizard.css';

// material components
import {
  IconButton,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TablePagination,
  TableRow,
  Divider,
} from '@material-ui/core';

// material icons
import EditIcon from '@material-ui/icons/Edit';
import PersonAddIcon from '@material-ui/icons/PersonAdd';
import ArrowBackIcon from '@material-ui/icons/ArrowBack';

// constants
import {
  DEFAULT_ROWS_PER_PAGE,
  MANAGE_USERS_TITLE,
  MANAGE_USERS_TOOLBAR_TITLE,
  ROWS_PER_PAGE_LABEL,
  USER_MANAGEMENT_HEAD_CELLS,
  DEFAULT_SORT_BY_HEAD_CELL,
  ROLE_TECH_ADMIN,
  ROLE_NUVE_MASTER_ADMIN,
  ADD_EMPLOYEE_LABEL,
} from './constants';
import { ASC, DESC } from '../../global/constants';
import { ROLE_PRESETS, inferRoleKey, roleRank } from './employeeRoles';

// redux
import withShipment from '../../../withShipment';
import {
  linkedUsersSelector,
  rolesSelector,
} from '../../../redux/selectors/userManagement';
import { rfidSelector, userInfoSelector } from '../../../redux/selectors/auth';
import {
  statusSelector,
  loadingSelector,
} from '../../../redux/selectors/global';
import {
  fetchLinkedUsers,
  editUser,
  deleteUser,
  getRoles,
  registerRFID,
  deleteRFID,
} from '../../../redux/actions/userManagement';
import { registerUser } from '../../../redux/actions/auth';
import {
  fetchPendingAccessForUsers,
  decideAccessRequest,
} from '../../../redux/actions/accessRequests';
import { pendingAccessForUsersSelector } from '../../../redux/selectors/accessRequests';
import { canSeeAccessRequests } from '../../alerts/constants';
import SettingsToolbar from '../SettingsToolbar';
import WriteGuard from '../../global/ReadOnly/WriteGuard';
import PropTypes from 'prop-types';
import CustomTableHeader from '../../global/Table/CustomTableHeader';
import GlobalTableHead from '../../global/Table/GlobalTableHead';
import DeleteUser from './DeleteUser';
import FeedbackBar from '../../feedbackBar/FeedbackBar';
import ErrorFeedbackBar from '../../global/ErrorFeedbackBar';
import { clearStatus } from '../../../redux/actions/global';
import { companySelector } from '../../../redux/selectors/account';
import { listCompanies } from '../../../redux/actions/settings';
import TopToolbar from '../../global/subcomponents/topToolbar/TopToolbar';
import BlockIcon from '@mui/icons-material/Block';
import { TabContext, TabList, TabPanel } from '@material-ui/lab';
import { Box, Tab } from '@material-ui/core';

class ManageUsersTable extends SearchableTable {
  constructor(props) {
    super(props);
    // Lets the toolbar's Save Changes button drive the edit form, which owns
    // the payload for every tab.
    this.wizardRef = React.createRef();
    this.state = {
      order: ASC,
      orderBy: DEFAULT_SORT_BY_HEAD_CELL,
      isSearch: false,
      searchValue: '',
      searchRows: 0,
      users: [],
      page: 0,
      rowsPerPage: DEFAULT_ROWS_PER_PAGE,
      isEdit: false,
      openDialog: false,
      isDelete: {},
      editingUser: {},
      open: false,
      rfid: '',
      feedbackBarOpen: false,
      feedbackStatus: '',
      feedbackMessage: '',
      tabValue: '1',
    };
  }

  componentDidMount() {
    this.props.listCompanies();
    const data = { locationId: this.props.userInfo.selectedLocationId };
    this.props.fetchLinkedUsers(data);
    this.props.getRoles({
      locationId: this.props.userInfo.selectedLocationId,
      type: 'employee',
    });
  }

  componentDidUpdate(prevProps) {
    if (
      this.props.status.statusText &&
      prevProps.status.statusText !== this.props.status.statusText
    ) {
      this.openFeedbackBar();
    }

    if (prevProps.rfid !== this.props.rfid) {
      this.setState({ rfid: this.props.rfid });
    }
  }

  getActiveUsers = () => this.props.users.filter((u) => u.enabled === true);
  getInactiveUsers = () => this.props.users.filter((u) => u.enabled === false);

  getUsersForTab = () => {
    const { tabValue } = this.state;
    if (tabValue === '1') return this.getActiveUsers();
    return this.getInactiveUsers();
  };

  openFeedbackBar = (status, message) => {
    this.setState({
      feedbackStatus: status,
      feedbackMessage: message,
      feedbackBarOpen: true,
    });
  };

  closeFeedbackBar = () => {
    this.setState(
      { feedbackBarOpen: false, feedbackStatus: '', feedbackMessage: '' },
      () => {
        this.props.clearStatus();
      }
    );
  };

  handleCloseDialog = () => {
    this.setState(
      { openDialog: false, isEdit: false, editingUser: {}, open: false },
      () => {
        const data = { locationId: this.props.userInfo.selectedLocationId };
        this.props.fetchLinkedUsers(data);
      }
    );
  };

  handleOpenDialog = () => {
    this.setState({ openDialog: true, isEdit: false, editingUser: {} });
  };

  handleRequestSort = (event, property) => {
    const isAsc = this.state.orderBy === property && this.state.order === ASC;
    this.setState({ order: isAsc ? DESC : ASC, orderBy: property });
  };

  onSearchValueChange = (event) => {
    this.setState({
      isSearch: event.target.value.length > 0,
      searchValue: event.target.value,
      page: 0,
    });
  };

  isSelected = (item) => this.state.users.indexOf(item) !== -1;

  saveEditingUser = () => {
    if (this.wizardRef.current && this.wizardRef.current.handleTopSave) {
      this.wizardRef.current.handleTopSave();
    }
  };

  // ---- Wizard save adapter ------------------------------------------------
  // `keepOpen` comes from the edit form's toolbar save: the admin is saving
  // mid-edit and expects to stay on the tab they are working in, so don't
  // tear the form down the way the final SAVE button does.
  handleWizardSave = (wizardPayload, keepOpen) => {
    if (this.state.isEdit) {
      const editingUser = this.state.editingUser || {};
      const isNewPassword = !!wizardPayload.password;
      const data = {
        locationId: this.props.userInfo.selectedLocationId,
        authorities: wizardPayload.authorities,
        change_password: isNewPassword,
        password: isNewPassword ? wizardPayload.password : null,
        firstname: wizardPayload.firstname,
        lastname: wizardPayload.lastname,
        new_displayname: wizardPayload.displayName,
        user_to_change: editingUser.username,
        timezone: wizardPayload.timezone,
        phone: wizardPayload.phone,
        rfid: wizardPayload.rfid,
        employeePerHour: wizardPayload.employeePerHour,
        employeeColor: wizardPayload.employeeColor,
        basePayWeekly: wizardPayload.basePayWeekly,
        managerThreshold: wizardPayload.managerThreshold,
        managerBonusPercent: wizardPayload.managerBonusPercent,
        investorPercent: wizardPayload.investorPercent,
        selectedLocations: wizardPayload.selectedLocations,
        // Per-location Full Control / View Only map from step 3. The edit
        // payload is built field-by-field rather than spread, so this has
        // to be listed explicitly or the wizard's setting is silently
        // dropped on save.
        locationAccess: wizardPayload.locationAccess,
        // Owner scope: track the company's linked locations automatically,
        // or keep a custom subset. Listed explicitly for the same reason as
        // locationAccess — this payload is built field-by-field, not spread.
        autoAllLocations: wizardPayload.autoAllLocations,
        enabled: wizardPayload.enabled,
        joinDate: editingUser.joinDate,
      };
      if (keepOpen) {
        this.props.editUser(data, { ...this.props.userInfo }, (open) => {
          this.handleSaveOpen(open);
          if (this.wizardRef.current && this.wizardRef.current.finishSaving) {
            this.wizardRef.current.finishSaving();
          }
          // The form stays up, so confirm the save here instead.
          this.openFeedbackBar('success', 'Changes saved.');
          this.props.fetchLinkedUsers({
            locationId: this.props.userInfo.selectedLocationId,
          });
          // The save may have queued a cross-company grant instead of
          // applying it — re-read so the row flips to "Requested" rather
          // than looking like the change was ignored.
          this.loadPendingAccess(editingUser.userId);
        });
        return;
      }
      this.props.editUser(
        data,
        { ...this.props.userInfo },
        this.handleSaveOpen
      );
      this.handleCloseDialog();
      return;
    }

    // Add mode
    const data = {
      ...wizardPayload,
      selectedLocationId: this.props.userInfo.selectedLocationId,
      admin: this.props.userInfo.username,
      is_linked_user: true,
      companyId: this.props.userInfo.company,
      company: this.props.userInfo.company,
    };
    const callback = (success, response) => {
      this.openFeedbackBar(success ? 'success' : 'error', response);
    };
    this.props.registerUser(data, callback);
    this.handleCloseDialog();
  };

  handleDeleteTrue = (index) => {
    this.setState({ isDelete: { ...this.state.isDelete, [index]: true } });
  };

  handleDeleteFalse = (index) => {
    this.setState({ isDelete: { ...this.state.isDelete, [index]: false } });
  };

  // /accessRequest/pendingForUsers is owner-tier only and answers anyone
  // else with 403, which AuthUtil turns into a logout. A manager who can
  // open the employee wizard must therefore never make the call - the
  // "Requested" chips are an owner-only affordance anyway.
  loadPendingAccess = (userId) => {
    if (!userId) return;
    if (!canSeeAccessRequests(this.props.userInfo)) return;
    this.props.fetchPendingAccessForUsers([userId]);
  };

  handleEdit = (user) => {
    this.setState({ openDialog: true, isEdit: true, editingUser: user });
    // A cross-company grant waiting on another owner is NOT on the user
    // record, so the wizard has to be told about it separately or the
    // location it was asked for simply would not appear.
    this.loadPendingAccess(user?.userId);
  };

  // Withdraw an outstanding request from the employee's own row. Refreshes
  // the wizard's chips through the same fetch that populated them.
  handleCancelAccessRequest = (request) => {
    this.props.decideAccessRequest(
      'cancel',
      {
        requestId: request.requestId,
        locationId: this.props.userInfo?.selectedLocationId,
        companyId: this.props.userInfo?.companyId,
      },
      (ok) => {
        if (!ok) return;
        this.openFeedbackBar('success', 'Request cancelled.');
        this.loadPendingAccess(this.state.editingUser?.userId);
      }
    );
  };

  handleSaveOpen = (open) => this.setState({ open });

  handleChangeRowsPerPage = (event) => {
    this.setState({ rowsPerPage: event.target.value, page: 0 });
  };

  handleChangePage = (event, newPage) => this.setState({ page: newPage });

  getDisplayed = (rows) => {
    if (!rows) return [];
    const startIndex = this.state.page * this.state.rowsPerPage;
    const endIndex = startIndex + this.state.rowsPerPage;
    return rows.slice(startIndex, endIndex);
  };

  // The Role column has no field to sort on — the role is DERIVED from
  // each user's authorities — so the inherited comparator (which reads
  // `user[orderBy]`) would compare undefined against undefined and leave
  // the table in API order. This sorts by seniority instead.
  //
  // Ties fall back to name, always ascending, so flipping the column
  // regroups the roles without also scrambling the names inside a group.
  getComparator = (order, orderBy) => {
    if (orderBy === 'role') {
      const direction = order === DESC ? -1 : 1;
      return (a, b) => {
        const diff =
          roleRank(this.getAuthorityStrings(a)) -
          roleRank(this.getAuthorityStrings(b));
        if (diff !== 0) return direction * diff;
        return (a.displayName || '').localeCompare(b.displayName || '');
      };
    }
    // Every other column keeps the inherited behaviour. Called through
    // `this.descendingComparator` rather than `super` because the parent
    // defines these as class fields (instance properties), which `super`
    // can't reach.
    return order === DESC
      ? (a, b) => this.descendingComparator(a, b, orderBy)
      : (a, b) => -this.descendingComparator(a, b, orderBy);
  };

  // Helper: read raw authority strings from a user record
  getAuthorityStrings = (user) =>
    (user.authorities || []).map((a) =>
      typeof a === 'string' ? a : a.authority
    );

  renderRoleBadge = (user) => {
    const auths = this.getAuthorityStrings(user);
    const roleKey = inferRoleKey(auths);
    const preset = ROLE_PRESETS[roleKey];
    return (
      <span className="mu-role-badge" style={{ background: preset.color }}>
        {preset.label}
      </span>
    );
  };

  renderTablePanel = () => {
    const users = this.getUsersForTab();

    return (
      <>
        <CustomTableHeader
          isSearchBar={false}
          searchText={this.state.searchValue}
          onSearchValueChange={this.onSearchValueChange}
          pagination={
            <TablePagination
              className="table-pagination"
              labelRowsPerPage={ROWS_PER_PAGE_LABEL}
              rowsPerPageOptions={[
                5,
                25,
                50,
                100,
                {
                  value: this.props.users.length,
                  label: 'All',
                },
              ]}
              count={!users ? 0 : this.filterBySearch(users).length}
              rowsPerPage={this.state.rowsPerPage}
              page={this.state.page}
              onChangePage={this.handleChangePage}
              onChangeRowsPerPage={this.handleChangeRowsPerPage}
            />
          }
        />
        <TableContainer>
          <Table
            aria-labelledby="tableTitle"
            size="medium"
            aria-label={MANAGE_USERS_TITLE}
          >
            <GlobalTableHead
              isCreatePurchaseOrderTable={false}
              isShipmentTable={false}
              headCells={USER_MANAGEMENT_HEAD_CELLS}
              order={this.state.order}
              orderBy={this.state.orderBy}
              onRequestSort={this.handleRequestSort}
              rowCount={!users ? 0 : users.length}
            />

            <TableBody>
              {this.getDisplayed(
                this.stableSort(
                  users,
                  this.getComparator(this.state.order, this.state.orderBy)
                )
              ).map((user, index) => {
                const itemKey = `mu-row-${index}`;
                return (
                  <TableRow
                    hover
                    role="checkbox"
                    tabIndex={-1}
                    key={user.userId}
                  >
                    {/* Deliberately NOT guarded. Opening an employee to
                        review their role, permissions and locations is a
                        read — the wizard itself goes read-only and drops
                        its SAVE at a VIEW-ONLY location, same as a bonus
                        the user didn't create. Only Add Employee and the
                        row delete are removed.
                        See constants/readOnlyAccess.js. */}
                    <TableCell align="center" scope="row">
                      <IconButton onClick={() => this.handleEdit(user)}>
                        <EditIcon />
                      </IconButton>
                    </TableCell>
                    <TableCell
                      align="left"
                      id={itemKey}
                      scope="row"
                      className="cellPadding"
                      padding="default"
                    >
                      {(user.displayName || '').replace(/\bnull\b/g, '').trim()}
                    </TableCell>
                    <TableCell
                      align="left"
                      scope="row"
                      className="cellPadding"
                      padding="default"
                    >
                      {user.username}
                    </TableCell>
                    <TableCell
                      align="left"
                      scope="row"
                      className="cellPadding"
                      padding="default"
                    >
                      {this.renderRoleBadge(user)}
                    </TableCell>
                    <DeleteUser
                      admin={this.props.userInfo}
                      user={user}
                      deleteUser={this.props.deleteUser}
                      isDelete={this.state.isDelete[index]}
                      handleDeleteTrue={this.handleDeleteTrue}
                      handleDeleteFalse={this.handleDeleteFalse}
                      index={index}
                      handleOpen={this.handleSaveOpen}
                    />
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </TableContainer>
      </>
    );
  };

  // Return path handed over by whichever page routed here (router state, so it
  // survives a re-render but not a hard refresh — on refresh there is simply no
  // Back control, which is the correct outcome for a directly-opened URL).
  get returnPath() {
    return this.props.location?.state?.from || null;
  }

  handleReturn = () => {
    if (this.returnPath) {
      this.props.history.push(this.returnPath);
    }
  };

  // Same control for the tech-admin-blocked view, which renders TopToolbar.
  getReturnMenuItems = () => {
    if (!this.returnPath) return null;
    return [
      {
        title: this.props.location?.state?.fromLabel || 'Back',
        icon: <ArrowBackIcon />,
        onClick: this.handleReturn,
      },
    ];
  };

  render() {
    const isTechAdminBlocked =
      this.props.userInfo.authority?.includes(ROLE_TECH_ADMIN) &&
      !this.props.userInfo.authority?.includes(ROLE_NUVE_MASTER_ADMIN) &&
      this.props.userInfo.selectedLocationName === 'NuVe System';

    return (
      <>
        <ErrorFeedbackBar />
        <FeedbackBar
          open={this.state.feedbackBarOpen}
          handleClose={this.closeFeedbackBar}
          severity={this.state.feedbackStatus}
          message={this.state.feedbackMessage}
        />
        <Paper className="paper">
          {/* Set when another page routed here and wants the user handed back
              (see UserProfile.handleManageEmployees). Absent on a normal visit
              from the sidebar, in which case no Back control is shown. */}
          {isTechAdminBlocked ? (
            <TopToolbar
              pageName={MANAGE_USERS_TOOLBAR_TITLE}
              menuItems={this.getReturnMenuItems()}
            />
          ) : (
            <SettingsToolbar
              handleClose={this.handleCloseDialog}
              handleSave={
                this.state.openDialog && this.state.isEdit
                  ? this.saveEditingUser
                  : undefined
              }
              title={
                this.state.openDialog
                  ? this.state.isEdit
                    ? 'Edit Employee'
                    : 'Add Employee'
                  : MANAGE_USERS_TOOLBAR_TITLE
              }
              addingOrEditing={this.state.openDialog}
              hideCreate
              onReturn={this.returnPath ? this.handleReturn : undefined}
              returnLabel={this.props.location?.state?.fromLabel}
            />
          )}

          {this.state.openDialog ? (
            <EmployeeWizard
              ref={this.wizardRef}
              mode={this.state.isEdit ? 'edit' : 'add'}
              user={this.state.isEdit ? this.state.editingUser : undefined}
              admin={this.props.userInfo}
              users={this.props.users}
              companies={this.props.companies}
              allRoles={this.props.roles}
              registerRFID={this.props.registerRFID}
              deleteRFID={this.props.deleteRFID}
              rfid={this.state.rfid}
              onSave={this.handleWizardSave}
              onClose={this.handleCloseDialog}
              pendingRequests={this.props.pendingAccessRequests}
              onCancelAccessRequest={this.handleCancelAccessRequest}
            />
          ) : isTechAdminBlocked ? (
            <div
              style={{
                display: 'flex',
                flexDirection: 'column',
                justifyContent: 'center',
                alignItems: 'center',
                height: '80vh',
                textAlign: 'center',
                color: 'black',
                padding: '20px',
              }}
            >
              <h1
                style={{
                  fontSize: '2.5rem',
                  marginBottom: '20px',
                  color: 'black',
                }}
              >
                <BlockIcon
                  style={{
                    fontSize: '3rem',
                    color: 'red',
                    marginRight: '16px',
                    marginTop: '-8px',
                  }}
                />
                Access Denied
              </h1>

              <p className="access-denied-text">
                Sorry, Tech Admins are not allowed to view this page.
              </p>
              <a href="/" className="access-denied-button">
                Go to Home
              </a>
            </div>
          ) : (
            <Box className="mu-list-shell">
              <TabContext value={this.state.tabValue}>
                <Box
                  sx={{
                    backgroundColor: '#e5eafd',
                    borderBottom: '1px solid #E0E0E0',
                    px: 2,
                    position: 'sticky',
                    // Pins directly under the toolbar. The literal 130 was a
                    // near-miss on the portrait-phone value (75 + 56) and sat
                    // 9px under the 64px desktop toolbar, which covered it.
                    top: 'var(--nds-sticky-top-2)',
                    zIndex: 1100,
                  }}
                >
                  <TabList
                    onChange={(e, val) =>
                      this.setState({ tabValue: val, page: 0 })
                    }
                    aria-label="employee tabs"
                    sx={{
                      minHeight: 48,
                      '& .MuiTabs-indicator': {
                        height: 3,
                        borderRadius: 2,
                        backgroundColor: '#3F51B5',
                      },
                    }}
                  >
                    <Tab
                      value="1"
                      disableRipple
                      label={
                        <Box display="flex" alignItems="center" gap={1}>
                          Employees
                          <Box
                            sx={{
                              ml: '16px',
                              px: 1.2,
                              py: 0.2,
                              fontSize: '12px',
                              fontWeight: 600,
                              borderRadius: '999px',
                              backgroundColor:
                                this.state.tabValue === '1'
                                  ? '#2765da'
                                  : '#a2b5d7',
                              color: '#fff',
                            }}
                          >
                            {this.getActiveUsers().length}
                          </Box>
                        </Box>
                      }
                      sx={{
                        textTransform: 'none',
                        fontWeight: 600,
                        fontSize: 14,
                        color: this.state.tabValue === '1' ? '#111' : '#6B7280',
                        minHeight: 48,
                      }}
                    />

                    <Divider
                      style={{
                        width: '1px',
                        height: '22px',
                        backgroundColor: '#a2b5d7',
                        alignSelf: 'center',
                        marginLeft: 4,
                        marginRight: 4,
                      }}
                    />

                    <Tab
                      value="2"
                      disableRipple
                      label={
                        <Box display="flex" alignItems="center" gap={1}>
                          Inactive Employees
                          <Box
                            sx={{
                              ml: '16px',
                              px: 1.2,
                              py: 0.2,
                              fontSize: '12px',
                              fontWeight: 600,
                              borderRadius: '999px',
                              backgroundColor:
                                this.state.tabValue === '2'
                                  ? '#3F51B5'
                                  : '#a2b5d7',
                              color: '#fff',
                            }}
                          >
                            {this.getInactiveUsers().length}
                          </Box>
                        </Box>
                      }
                      sx={{
                        textTransform: 'none',
                        fontWeight: 600,
                        fontSize: 14,
                        color: this.state.tabValue === '2' ? '#111' : '#6B7280',
                        minHeight: 48,
                      }}
                    />
                  </TabList>
                </Box>

                <Box className="mu-list-scroll">
                  <TabPanel value="1" style={{ margin: 0, padding: 0 }}>
                    {this.renderTablePanel()}
                  </TabPanel>
                  <TabPanel value="2" style={{ margin: 0, padding: 0 }}>
                    {this.renderTablePanel()}
                  </TabPanel>
                </Box>

                <WriteGuard>
                  <div className="mu-add-bar">
                    <button
                      className="mu-add-btn"
                      onClick={this.handleOpenDialog}
                    >
                      <PersonAddIcon />
                      {ADD_EMPLOYEE_LABEL}
                    </button>
                  </div>
                </WriteGuard>
              </TabContext>
            </Box>
          )}
        </Paper>
      </>
    );
  }
}

const mapStateToProps = (state) => ({
  users: linkedUsersSelector(state),
  status: statusSelector(state),
  loading: loadingSelector(state),
  userInfo: userInfoSelector(state),
  roles: rolesSelector(state),
  rfid: rfidSelector(state),
  companies: companySelector(state),
  pendingAccessRequests: pendingAccessForUsersSelector(state),
});

const actionCreators = {
  fetchLinkedUsers,
  editUser,
  registerUser,
  deleteUser,
  getRoles,
  registerRFID,
  deleteRFID,
  clearStatus,
  listCompanies,
  fetchPendingAccessForUsers,
  decideAccessRequest,
};

ManageUsersTable.propTypes = {
  users: PropTypes.array,
  status: PropTypes.object,
  loading: PropTypes.bool,
  userInfo: PropTypes.object,
};

ManageUsersTable.defaultProps = {
  users: [],
  status: {},
  loading: false,
  userInfo: {},
};

export default withShipment(
  {
    mapStateToProps,
    actionCreators,
  },
  ManageUsersTable
);
